import {TypeOperation} from "../operation";

/**
 * Leitura de um passo (operação) só para exibição. Nada aqui altera o que é salvo:
 * nome, URL, seletor e valor continuam exatamente como estão no caso de teste.
 *
 * Os nomes seguem o padrão "[CATEGORIA] [AÇÃO] - Nome" (ex.: "[CHECKOUT] [CLIQUE] - Abrir Login").
 * Os prefixos entre colchetes viram etiquetas e o resto vira o título do passo.
 */

export interface DetalheOperacao {
    rotulo: string,
    valor: string
}

export interface OperacaoExibicao {
    titulo: string,
    categorias: Array<string>,
    tipo: string | null,
    detalhes: Array<DetalheOperacao>
}

const ROTULOS_TIPO: Record<string, string> = {
    [TypeOperation.TYPE_CALL_URL]: 'Acessar URL',
    [TypeOperation.TYPE_INPUT_DATA]: 'Informar valor',
    [TypeOperation.TYPE_CLICK]: 'Clicar',
    [TypeOperation.TYPE_VALIDATE_MESSAGE]: 'Validar mensagem',
    [TypeOperation.TYPE_WAIT]: 'Aguardar',
    [TypeOperation.TYPE_CLOSE]: 'Fechar janela',
    [TypeOperation.TYPE_EXECUTE_SCRIPT]: 'Executar script',
};

// Prefixos que só repetem o tipo de ação: não viram etiqueta de categoria
const PREFIXOS_DE_ACAO: Record<string, string> = {
    CLIQUE: TypeOperation.TYPE_CLICK,
    CLICK: TypeOperation.TYPE_CLICK,
    INSERCAO: TypeOperation.TYPE_INPUT_DATA,
    INPUT: TypeOperation.TYPE_INPUT_DATA,
    VALIDACAO: TypeOperation.TYPE_VALIDATE_MESSAGE,
    ACESSO: TypeOperation.TYPE_CALL_URL,
    URL: TypeOperation.TYPE_CALL_URL,
    WAIT: TypeOperation.TYPE_WAIT,
    AGUARDAR: TypeOperation.TYPE_WAIT,
    WINDOW: TypeOperation.TYPE_CLOSE,
    FECHAR: TypeOperation.TYPE_CLOSE,
    SCRIPT: TypeOperation.TYPE_EXECUTE_SCRIPT,
};

// Palavras que costumam vir sem acento nos prefixos
const COM_ACENTO: Record<string, string> = {
    INSERCAO: 'Inserção',
    VALIDACAO: 'Validação',
    ACAO: 'Ação',
    OPCAO: 'Opção',
    INFORMACAO: 'Informação',
    CONFIGURACAO: 'Configuração',
    NAVEGACAO: 'Navegação',
    PAGINA: 'Página',
};

const semAcento = (texto: string) => texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().trim();

const formatarEtiqueta = (texto: string) => {
    const chave = semAcento(texto);
    if (COM_ACENTO[chave]) {
        return COM_ACENTO[chave];
    }
    // Siglas curtas (URL, LGPD, API) ficam como estão
    if (/^[A-Z0-9]{2,4}$/.test(texto.trim())) {
        return texto.trim();
    }
    const minusculo = texto.trim().toLowerCase();
    return minusculo.charAt(0).toUpperCase() + minusculo.slice(1);
}

/** Título todo em maiúsculas vira frase normal; o resto fica como foi escrito */
const suavizarTitulo = (titulo: string) => {
    const letras = titulo.replace(/[^A-Za-zÀ-ÿ]/g, '');
    if (letras.length > 3 && letras === letras.toUpperCase()) {
        const minusculo = titulo.toLowerCase();
        return minusculo.charAt(0).toUpperCase() + minusculo.slice(1);
    }
    return titulo;
}

const texto = (valor: any) => (valor === undefined || valor === null ? '' : String(valor).trim());

const ROTULO_BUSCA: Record<string, string> = {class: 'Classe', name: 'Name', css: 'CSS', xpath: 'XPath'};

export const rotuloTipoOperacao = (tipo: string | null | undefined) => (tipo ? ROTULOS_TIPO[tipo] || tipo : null);

export const descreverOperacao = (operacao: any): OperacaoExibicao => {
    const nome = texto(operacao?.name);
    const params = operacao?.params || {};
    const tipoOperacao: string = params.type || '';

    let titulo = nome;
    let prefixos: Array<string> = [];
    const partes = nome.match(/^\s*((?:\[[^\]]*]\s*)+)(?:[-–:]\s*)?(.*)$/);
    if (partes) {
        prefixos = (partes[1].match(/\[([^\]]*)]/g) || []).map(prefixo => prefixo.slice(1, -1).trim()).filter(Boolean);
        titulo = partes[2].trim() || prefixos.join(' ');
    }

    const categorias = prefixos
        .filter(prefixo => PREFIXOS_DE_ACAO[semAcento(prefixo)] !== tipoOperacao)
        .map(formatarEtiqueta);

    const detalhes: Array<DetalheOperacao> = [];
    const url = texto(params.url);
    const seletor = texto(params.value);
    const valor = texto(params.data);

    if (url) {
        detalhes.push({rotulo: 'URL', valor: url});
    }
    if (seletor) {
        const rotulo = tipoOperacao === TypeOperation.TYPE_CALL_URL
            ? 'URL'
            : (ROTULO_BUSCA[texto(params.type_term)] || texto(params.type_term) || 'Seletor');
        detalhes.push({rotulo, valor: seletor});
    }
    if (valor) {
        const rotulos: Record<string, string> = {
            [TypeOperation.TYPE_INPUT_DATA]: 'Valor',
            [TypeOperation.TYPE_VALIDATE_MESSAGE]: 'Texto esperado',
            [TypeOperation.TYPE_WAIT]: 'Segundos',
            [TypeOperation.TYPE_EXECUTE_SCRIPT]: 'Script',
            [TypeOperation.TYPE_CALL_URL]: 'URL',
        };
        detalhes.push({rotulo: rotulos[tipoOperacao] || 'Valor', valor});
    }

    return {
        titulo: suavizarTitulo(titulo || 'Passo sem nome'),
        categorias,
        tipo: rotuloTipoOperacao(tipoOperacao),
        detalhes,
    };
}
